import type { KidInCustomer } from '@brighterly/lib-core-types/kid/interfaces'

import { Discipline } from '@brighterly/lib-core-types/discipline/enums'

import { trackEvent } from '@shared/lib/amplitude'

import { useKidRoutes } from './kid-route.hook'
import { useCustomerStore } from './customer-store/store'
import { useKidStore } from './kid-store/store'
import { useDisciplineStore } from './discipline-store/store'

interface KidsMenuReturn {
  kids: ComputedRef<KidInCustomer[]>
  activeKid: ComputedRef<KidInCustomer | null>
  setRedirect: (kid: KidInCustomer) => void
}

export const useKidsMenu = (): KidsMenuReturn => {
  const customerStore = useCustomerStore()
  const kidStore = useKidStore()
  const disciplineStore = useDisciplineStore()

  const {
    getHomeMath,
    getHomeEla,
    isRouteDisciplineEla,
  } = useKidRoutes()

  const kids = computed(() => customerStore.customer?.kids || [])

  const activeKid = computed(() => {
    return kids.value.find(kid => kid.id === kidStore.activeKidId) || null
  })

  // Sent before the URL switches the kid, so the picked kid is passed over the tracker's.
  const setTrackEvent = (kidId: number, url: string | null): void => {
    trackEvent('Field_change', { feature: 'navigation', name: 'kid_switcher', kid_id: kidId, url })
  }

  const setRedirect = async (kid: KidInCustomer): Promise<void> => {
    const subject = disciplineStore.getDisciplineCode ?? (isRouteDisciplineEla.value ? Discipline.ELA : Discipline.Math)
    const url = subject === Discipline.ELA ? getHomeEla(kid.id) : getHomeMath(kid.id)

    setTrackEvent(kid.id, url)
    await navigateTo(url)
  }

  return {
    kids,
    activeKid,
    setRedirect,
  }
}
